import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import EcoCreditsManager from "../../../services/home/EcoCreditsManager";
import AddAddressModal from "../addressComponents/AddAddressModal";
import UserTestimonials from "../userProfileComponents/UserTestimonials";
import UserBookSuggestions from "../userProfileComponents/UserBookSuggestions";
import UploadPhotoModal from "./UploadPhotoModal";
import AuthManager from "../../../services/dashboard/AuthManager";
import config from "../../../config";
import { resetCart } from "../../../features/cartSlice";
import { logout, updateUser } from "../../../features/authSlice";
import { persistor } from "../../../app/store";
import { resetSubscription } from "../../../features/subscriptionSlice";
import {
  Mail,
  Phone,
  ShieldCheck,
  Globe,
  ShoppingCart,
  Heart,
  Package,
  LogOut,
  CreditCard,
  Pen,
  MapPin,
  Award,
  Gift,
  Leaf,
  User,
  Copy,
  Check,
  ChevronRight,
  Plus,
  MessageSquare,
  HelpCircle,
  Sparkles,
  TreePine,
  CheckCircle2,
} from "lucide-react";
import { FaWhatsapp, FaInstagram } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

const UserProfile = () => {
  const [activeTab, setActiveTab] = useState("details");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [isPlanting, setIsPlanting] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  const [couponModal, setCouponModal] = useState({
    visible: false,
    code: "",
    expiry: "",
  });

  const token = useSelector((s) => s.auth.token);
  const user = useSelector((s) => s.auth.user) || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = () => {
    persistor.purge();
    dispatch(resetCart());
    dispatch(resetSubscription());
    dispatch(logout());
    navigate("/login");
  };

  const handleUpload = async (file) => {
    try {
      await AuthManager.uploadProfilePhoto(user.userId, file, token);
      const res = await AuthManager.getUserDetails(user.userId, token);
      dispatch(updateUser(res.responseData));
      setIsModalOpen(false);
      toast.success("Profile photo updated successfully!");
    } catch {
      toast.error("Upload failed. Please try again.");
    }
  };

  // Eco credits calculations
  const eco = user.ecoCredits || 0;
  const trees = Math.floor(eco / 50);
  const nextCreditsForTree = (trees + 1) * 500 - eco;
  const milestoneProgress = Math.min(100, Math.round(((eco % 500) / 500) * 100));

  useEffect(() => {
    const refreshUser = async () => {
      if (!user?.userId || !token) return;
      try {
        const res = await AuthManager.getUserDetails(user.userId, token);
        dispatch(updateUser(res.responseData));
      } catch (err) {
        console.error("Failed to refresh user", err);
      }
    };
    refreshUser();
  }, [user?.userId, token, dispatch]);

  const parseDateFromArray = (arr) => {
    if (!Array.isArray(arr)) return new Date();
    const [year, month, day, hour = 0, min = 0, sec = 0] = arr;
    return new Date(year, month - 1, day, hour, min, sec);
  };

  const handleRedeemEcoCredits = async () => {
    if (eco < 500) {
      toast.error("You need at least 500 EcoCredits to redeem.");
      return;
    }

    try {
      setIsRedeeming(true);
      const coupon = await EcoCreditsManager.redeemEcoCredits(user.userId);
      toast.success(`Coupon ${coupon.couponCode} created!`);

      setCouponModal({
        visible: true,
        code: coupon.couponCode,
        expiry: parseDateFromArray(coupon.expiryDate).toLocaleDateString(),
      });

      const updatedUser = await AuthManager.getUserDetails(user.userId, token);
      dispatch(updateUser(updatedUser.responseData));
    } catch (err) {
      toast.error(err.message || "Redemption failed.");
    } finally {
      setIsRedeeming(false);
    }
  };

  const handlePlantTree = async () => {
    if (eco < 500) {
      toast.error("You need at least 500 EcoCredits to plant a tree.");
      return;
    }

    try {
      setIsPlanting(true);
      await EcoCreditsManager.plantTree(
        user.userId,
        500,
        "Planting via user profile"
      );
      toast.success("A tree has been planted on your behalf! 🌳");

      const updatedUser = await AuthManager.getUserDetails(user.userId, token);
      dispatch(updateUser(updatedUser.responseData));
    } catch (err) {
      toast.error(err.message || "Tree planting failed.");
    } finally {
      setIsPlanting(false);
    }
  };

  const copyReferral = () => {
    if (!user.referralCode) return;
    navigator.clipboard.writeText(user.referralCode);
    setCopiedCode(true);
    toast.success("Referral code copied to clipboard!");
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const tabs = [
    { id: "details", label: "Profile & Account", icon: User },
    { id: "ecocredits", label: "EcoCredits & Rewards", icon: Leaf, badge: eco > 0 ? `${eco}` : null },
    { id: "activity", label: "Reviews & Suggestions", icon: MessageSquare },
    { id: "faqs", label: "Eco FAQs", icon: HelpCircle },
  ];

  return (
    <div className="min-h-screen bg-slate-50/70 py-8 px-4 sm:px-6 lg:px-8 font-nunito">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* ============================================================ */}
        {/* 1. COMPACT & PROFESSIONAL PROFILE BANNER                      */}
        {/* ============================================================ */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden">
          {/* Subtle Branded Cover Banner */}
          <div className="h-28 sm:h-36 bg-gradient-to-r from-orange-600 via-[#FF7236] to-amber-500 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]" />
            <div className="absolute -right-8 -bottom-8 w-44 h-44 bg-white/15 rounded-full blur-2xl pointer-events-none" />
          </div>

          {/* Profile Identity Row */}
          <div className="px-6 pb-6 pt-2 sm:pt-0 relative">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              {/* Avatar & Info */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
                {/* Avatar with Camera Badge */}
                <div className="relative group self-center sm:self-auto -mt-12 sm:-mt-16 flex-shrink-0">
                  <img
                    src={
                      user.profilePhotoUrl
                        ? `${config.url}/userImages/${user.profilePhotoUrl}`
                        : `https://ui-avatars.com/api/?size=160&name=${encodeURIComponent(
                            (user.firstName || "User") + " " + (user.lastName || "")
                          )}&background=FF7236&color=fff&bold=true`
                    }
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = `https://ui-avatars.com/api/?size=160&name=${encodeURIComponent(
                        (user.firstName || "User") + " " + (user.lastName || "")
                      )}&background=FF7236&color=fff&bold=true`;
                    }}
                    alt="Profile"
                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-full ring-4 ring-white shadow-lg object-cover bg-white"
                  />
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="absolute bottom-0 right-0 bg-white hover:bg-orange-50 text-gray-700 hover:text-primary p-2 rounded-full shadow-md border border-gray-200 transition-all hover:scale-105"
                    title="Change profile photo"
                  >
                    <Pen className="w-3.5 h-3.5 text-primary" />
                  </button>
                </div>

                {/* Name, Badges & ID */}
                <div className="text-center sm:text-left space-y-2 mt-1 sm:mt-2">
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2.5">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                      {user.firstName} {user.lastName}
                    </h1>
                   
                  </div>

                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 text-xs text-gray-500">
                    <span className="font-mono bg-slate-100 text-slate-700 px-2.5 py-0.5 rounded-md border border-slate-200 font-medium">
                      ID: #{user.userId}
                    </span>
                    {user.role && (
                      <span className="bg-orange-50 text-orange-700 px-2.5 py-0.5 rounded-md border border-orange-200 font-medium uppercase text-[11px] tracking-wide">
                        {user.role.replace("ROLE_", "")}
                      </span>
                    )}
                    {user.platform && (
                      <span className="bg-blue-50 text-blue-700 px-2.5 py-0.5 rounded-md border border-blue-200 font-medium text-[11px]">
                        {user.platform}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Action: Sign Out */}
              <div className="flex items-center justify-center sm:justify-end mt-2 sm:mt-0">
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold text-slate-600 hover:text-red-600 hover:bg-red-50 border border-slate-200 hover:border-red-200 transition-all shadow-xs"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================ */}
        {/* 2. QUICK HUB ACTION TILES (STANDARD & FUNCTIONAL)            */}
        {/* ============================================================ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
          <QuickActionTile
            icon={<CreditCard className="w-5 h-5 text-orange-600" />}
            bgColor="bg-orange-50"
            title="Subscriptions"
            subtitle="View & Renew Plan"
            onClick={() => navigate("/subscription-details")}
          />
          <QuickActionTile
            icon={<Package className="w-5 h-5 text-blue-600" />}
            bgColor="bg-blue-50"
            title="My Orders"
            subtitle="Track Book Orders"
            onClick={() => navigate("/myOrders")}
          />
          <QuickActionTile
            icon={<Heart className="w-5 h-5 text-rose-600" />}
            bgColor="bg-rose-50"
            title="Wishlist"
            subtitle="Saved Books"
            onClick={() => navigate("/Wishlist")}
          />
          <QuickActionTile
            icon={<ShoppingCart className="w-5 h-5 text-emerald-600" />}
            bgColor="bg-emerald-50"
            title="My Cart"
            subtitle="Books to Rent"
            onClick={() => navigate("/Cart")}
          />
        </div>

        {/* ============================================================ */}
        {/* 3. MODERN TAB NAVIGATION BAR                                 */}
        {/* ============================================================ */}
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-1.5 flex flex-wrap gap-1.5">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 min-w-[130px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                  isActive
                    ? "bg-primary text-white shadow-sm"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? "text-white" : "text-slate-500"}`} />
                <span>{tab.label}</span>
                {tab.badge && (
                  <span
                    className={`ml-1 px-1.5 py-0.2 rounded-full text-[11px] font-bold ${
                      isActive
                        ? "bg-white/20 text-white"
                        : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    }`}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ============================================================ */}
        {/* 4. TAB CONTENTS                                              */}
        {/* ============================================================ */}
        <AnimatePresence mode="wait">
          {activeTab === "details" && (
            <motion.div
              key="details"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >
              {/* Personal Information Card */}
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
                <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-100">
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-slate-900">
                      Personal Information
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Your personal and contact credentials on Books At Doorstep
                    </p>
                  </div>
                  <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Verified Member
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  <DetailItem
                    icon={<User className="w-4 h-4 text-primary" />}
                    label="Full Name"
                    value={`${user.firstName || "N/A"} ${user.lastName || ""}`}
                  />
                  <DetailItem
                    icon={<Mail className="w-4 h-4 text-primary" />}
                    label="Email Address"
                    value={user.emailId || "N/A"}
                  />
                  <DetailItem
                    icon={<Phone className="w-4 h-4 text-primary" />}
                    label="Mobile Number"
                    value={user.mobileNumber || "N/A"}
                  />
                  <DetailItem
                    icon={<ShieldCheck className="w-4 h-4 text-primary" />}
                    label="Account Role"
                    value={user.role?.replace("ROLE_", "") || "USER"}
                  />
                  <DetailItem
                    icon={<CheckCircle2 className="w-4 h-4 text-primary" />}
                    label="Membership Status"
                    value={user.userStatus || "ACTIVE"}
                  />
                  <DetailItem
                    icon={<Globe className="w-4 h-4 text-primary" />}
                    label="App / Platform"
                    value={user.platform || "WEB"}
                  />
                </div>
              </div>

              {/* Delivery Address Card */}
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
                <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-slate-900">
                      Delivery Address
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Where your rented books are picked up and delivered
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setEditingAddress(user.address || null);
                      setIsAddressModalOpen(true);
                    }}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary-shade4 bg-orange-50 hover:bg-orange-100/70 border border-orange-200/70 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    {user.address?.fullAddress ? (
                      <>
                        <Pen className="w-3.5 h-3.5" />
                        <span>Edit Address</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-3.5 h-3.5" />
                        <span>Add Address</span>
                      </>
                    )}
                  </button>
                </div>

                {user.address?.fullAddress ? (
                  <div className="flex items-start gap-3.5 p-4 rounded-xl bg-slate-50 border border-slate-200/70">
                    <div className="p-2 rounded-lg bg-orange-50 text-primary mt-0.5">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                          Default Address
                        </span>
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 rounded">
                          Active
                        </span>
                      </div>
                      <p className="text-sm font-medium text-slate-800 leading-relaxed">
                        {user.address.fullAddress}
                      </p>
                      {(user.address.city || user.address.pincode) && (
                        <p className="text-xs text-slate-500">
                          {[user.address.city?.cityName, user.address.pincode?.pincode]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 px-4 bg-slate-50/60 rounded-xl border border-dashed border-slate-200">
                    <MapPin className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-slate-700">
                      No delivery address saved yet
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 max-w-sm mx-auto">
                      Add your address so our delivery executives know where to deliver your next favorite read.
                    </p>
                    <button
                      onClick={() => {
                        setEditingAddress(null);
                        setIsAddressModalOpen(true);
                      }}
                      className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary-shade4 text-white text-xs font-bold rounded-lg shadow-sm transition-all"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Delivery Address
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === "ecocredits" && (
            <motion.div
              key="ecocredits"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >
              {/* EcoCredits Metrics Bar */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Total Credits */}
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      EcoCredits Balance
                    </span>
                    <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                      <Leaf className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-extrabold text-slate-900">{eco}</span>
                    <span className="text-xs text-slate-500">credits</span>
                  </div>
                  <button
                    onClick={handleRedeemEcoCredits}
                    disabled={isRedeeming || eco < 500}
                    className="w-full py-2 px-3 rounded-lg text-xs font-bold text-white bg-primary hover:bg-primary-shade4 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    {isRedeeming ? "Redeeming..." : "Redeem (Min 500)"}
                  </button>
                </div>

                {/* Trees Planted */}
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Trees Planted
                    </span>
                    <div className="p-2 rounded-lg bg-green-50 text-green-600">
                      <TreePine className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-extrabold text-slate-900">{trees}</span>
                    <span className="text-xs text-slate-500">trees contributed</span>
                  </div>
                  <button
                    onClick={handlePlantTree}
                    disabled={isPlanting || eco < 500}
                    className="w-full py-2 px-3 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    {isPlanting ? "Planting..." : "Plant a Tree (500 pts)"}
                  </button>
                </div>

                {/* Next Milestone */}
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Next Tree Goal
                    </span>
                    <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
                      <Award className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs font-medium">
                      <span className="text-slate-600">{eco % 500} / 500 pts</span>
                      <span className="text-amber-600 font-bold">{nextCreditsForTree} to go</span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-amber-500 rounded-full transition-all duration-500"
                        style={{ width: `${milestoneProgress}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-tight">
                    Every 500 EcoCredits plants 1 verified native tree on your behalf.
                  </p>
                </div>
              </div>

              {/* Refer & Earn Card */}
              {user.referralCode && (
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl border border-orange-200/70 p-6 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-800 mb-1">
                        <Gift className="w-3.5 h-3.5" />
                        Refer & Earn 50 Credits
                      </div>
                      <h3 className="text-base font-bold text-slate-900">
                        Share Books At Doorstep with Friends
                      </h3>
                      <p className="text-xs text-slate-600 mt-0.5">
                        Give friends easy doorstep reading. When they subscribe using your code, you earn 50 EcoCredits!
                      </p>
                    </div>

                    <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-orange-200 shadow-sm self-start sm:self-auto">
                      <span className="font-mono font-bold text-sm text-slate-800 tracking-wider">
                        {user.referralCode}
                      </span>
                      <button
                        onClick={copyReferral}
                        className="p-1.5 rounded-md hover:bg-orange-50 text-primary transition-colors"
                        title="Copy code"
                      >
                        {copiedCode ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Quick Share Links */}
                  <div className="flex items-center gap-2.5 pt-2 border-t border-orange-200/50 flex-wrap">
                    <span className="text-xs font-semibold text-slate-600 mr-1">Share via:</span>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(
                        `Join BooksAtDoorstep and get free book rentals! Use my referral code: ${user.referralCode} https://booksatdoorsteps.com`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition"
                    >
                      <FaWhatsapp className="w-3.5 h-3.5" />
                      <span>WhatsApp</span>
                    </a>
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                        `Join BooksAtDoorstep and get free book rentals! Use my referral code: ${user.referralCode} https://booksatdoorsteps.com`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-slate-900 hover:bg-black text-white shadow-sm transition"
                    >
                      <FaXTwitter className="w-3.5 h-3.5" />
                      <span>X / Twitter</span>
                    </a>
                    <a
                      href="https://www.instagram.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-pink-600 hover:bg-pink-700 text-white shadow-sm transition"
                    >
                      <FaInstagram className="w-3.5 h-3.5" />
                      <span>Instagram</span>
                    </a>
                  </div>
                </div>
              )}

              {/* Ways to Earn Credits Grid */}
              <div className="bg-white rounded-2xl border border-slate-200/80 p-6 space-y-4">
                <h3 className="text-base font-bold text-slate-900">
                  How to Earn More EcoCredits
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <EarnCreditCard
                    image="/images/rent book.svg"
                    title="Rent a Book"
                    reward="+20 Credits"
                    desc="Read sustainably by borrowing physical books"
                  />
                  <EarnCreditCard
                    image="/images/refer friend.svg"
                    title="Refer a Friend"
                    reward="+50 Credits"
                    desc="Invite friends to start reading with your code"
                  />
                  <EarnCreditCard
                    image="/images/leave review.svg"
                    title="Leave a Review"
                    reward="+10 Credits"
                    desc="Share your reading experience on books"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "activity" && (
            <motion.div
              key="activity"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
                <UserTestimonials />
              </div>
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
                <UserBookSuggestions />
              </div>
            </motion.div>
          )}

          {activeTab === "faqs" && (
            <motion.div
              key="faqs"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-3"
            >
              <div className="pb-3 mb-2 border-b border-slate-100">
                <h2 className="text-base sm:text-lg font-bold text-slate-900">
                  Frequently Asked Questions — EcoCredits
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Everything you need to know about points, tree planting, and rewards
                </p>
              </div>

              {[
                {
                  q: "What are EcoCredits?",
                  a: "EcoCredits are green reward points you earn by taking sustainable reading actions through BooksAtDoorstep, such as renting books, referring friends, or leaving honest book reviews.",
                },
                {
                  q: "Why should I care about EcoCredits?",
                  a: "EcoCredits allow you to unlock subscription discounts and contribute directly to real native tree planting. Every 500 credits equals one tree planted on your behalf.",
                },
                {
                  q: "How do I earn EcoCredits?",
                  a: "You earn +20 credits each time you rent a book, +50 credits when a referred friend signs up, and +10 credits for reviewing a book you read.",
                },
                {
                  q: "How does the tree planting work?",
                  a: "When you redeem 500 EcoCredits, we either deliver a sapling with your next book or plant a tree in a public area on your behalf, complete with photographic updates.",
                },
                {
                  q: "Do my EcoCredits ever expire?",
                  a: "No! Your EcoCredits never expire and remain safe in your account for as long as your account is active.",
                },
              ].map((faq, i) => (
                <details
                  key={i}
                  className="group rounded-xl border border-slate-200/80 p-4 transition-colors hover:border-orange-200 open:bg-slate-50/50"
                >
                  <summary className="cursor-pointer font-semibold text-sm text-slate-800 list-none flex items-center justify-between">
                    <span>{faq.q}</span>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="text-xs sm:text-sm text-slate-600 mt-2 leading-relaxed">
                    {faq.a}
                  </p>
                </details>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

      </div>

      {/* ============================================================ */}
      {/* 5. MODALS                                                    */}
      {/* ============================================================ */}
      <UploadPhotoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpload={handleUpload}
      />

      <AddAddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        userId={user.userId}
        initialData={editingAddress}
        onSuccess={async () => {
          const updatedUser = await AuthManager.getUserDetails(
            user.userId,
            token
          );
          dispatch(updateUser(updatedUser.responseData));
        }}
      />

      {couponModal.visible && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-sm text-center space-y-4 animate-in fade-in zoom-in-95 duration-200">
            <div className="w-12 h-12 rounded-full bg-orange-100 text-primary flex items-center justify-center mx-auto">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">
                Coupon Generated!
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Apply this coupon on your next subscription checkout:
              </p>
            </div>
            <div className="flex justify-center items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-2">
              <span className="font-mono font-bold text-slate-800 text-sm tracking-wider">
                {couponModal.code}
              </span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(couponModal.code);
                  toast.success("Coupon code copied!");
                }}
                className="text-xs font-semibold bg-primary text-white px-2.5 py-1 rounded-lg hover:bg-primary-shade4 transition"
              >
                Copy
              </button>
            </div>
            <p className="text-[11px] text-slate-400">
              Valid until: {couponModal.expiry}
            </p>
            <button
              onClick={() => setCouponModal({ ...couponModal, visible: false })}
              className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2 rounded-lg text-xs transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// SUBCOMPONENTS (CLEAN & NON-BULKY)
// ============================================================
const QuickActionTile = ({ icon, bgColor, title, subtitle, onClick }) => (
  <button
    onClick={onClick}
    className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md hover:border-orange-300 transition-all text-left flex items-center justify-between group"
  >
    <div className="flex items-center gap-3">
      <div className={`p-2.5 rounded-xl ${bgColor} flex-shrink-0`}>
        {icon}
      </div>
      <div>
        <p className="text-xs sm:text-sm font-bold text-slate-900 group-hover:text-primary transition-colors">
          {title}
        </p>
        <p className="text-[11px] text-slate-500 line-clamp-1">{subtitle}</p>
      </div>
    </div>
    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-primary group-hover:translate-x-0.5 transition-all hidden sm:block flex-shrink-0" />
  </button>
);

const DetailItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50/70 border border-slate-100">
    <div className="p-2 rounded-lg bg-white shadow-xs border border-slate-100 text-primary mt-0.5">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
        {label}
      </p>
      <p className="text-sm font-semibold text-slate-800 truncate mt-0.5">
        {value}
      </p>
    </div>
  </div>
);

const EarnCreditCard = ({ image, title, reward, desc }) => (
  <div className="p-4 rounded-xl border border-slate-100 bg-slate-50/50 flex flex-col items-center text-center space-y-2">
    <img src={image} alt={title} className="h-16 w-auto object-contain" />
    <div>
      <p className="text-xs font-bold text-slate-900">{title}</p>
      <span className="inline-block text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full mt-0.5">
        {reward}
      </span>
      <p className="text-[11px] text-slate-500 mt-1 leading-snug">{desc}</p>
    </div>
  </div>
);

export default UserProfile;
